import { useLocation, Link } from "@/lib/router-compat";
import { useEffect } from "react";
import { logger } from '@/lib/logger';
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import LegacyGone from "@/components/LegacyGone";
import Redirect from "@/components/Redirect";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft } from "lucide-react";

// Legacy URL patterns that React Router cannot match as Routes (file extensions,
// odd literal paths from old crawler indexes). When NotFound renders for one of
// these, we render LegacyGone instead so Google sees "permanently removed"
// + noindex,nofollow instead of generic 404 messaging it reads as soft 404.
const LEGACY_URL_PATTERN = /(\.(cfm|asp|htm)(\?|$|\/))|^\/build1[./]|^\/wp-[^/]*\.php(\?|$)|\*$/i;

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    logger.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  // Trailing-slash normalization: GSC flags /about/, /careers/, /contact/,
  // /home/, /sitemap/ as soft 404 / duplicate-canonical because React Router 6
  // treats /about and /about/ as different paths. Redirect to the canonical
  // (slash-less) version. Root "/" is exempt.
  if (location.pathname !== '/' && location.pathname.endsWith('/')) {
    return <Redirect to={location.pathname.replace(/\/+$/, '')} />;
  }

  if (LEGACY_URL_PATTERN.test(location.pathname)) {
    return <LegacyGone />;
  }

  return (
    <div className="min-h-dvh bg-background flex flex-col font-sans">
      <SEO
        title="Page Not Found (404)"
        description="The page you're looking for doesn't exist. Return to AKIVA's homepage to explore our AI engineering and consulting services."
        robots="noindex, follow"
      />
      <Navigation />
      
      <main className="flex-grow flex items-center justify-center relative overflow-hidden min-h-[70vh]">
        {/* Ambient Background Elements */}
        <div className="absolute inset-0 bg-gradient-akiva opacity-30 z-0"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl z-0"></div>
        
        <div className="container-max px-6 relative z-10 py-20 text-center">
          <div className="mb-2 animate-fade-in">
            <h1 className="text-[10rem] leading-none font-bold text-transparent bg-clip-text bg-gradient-to-b from-muted-foreground/10 to-transparent select-none">
              404
            </h1>
          </div>
          
          <div className="space-y-6 max-w-2xl mx-auto animate-fade-in-up -mt-16 relative z-20">
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-4">
              Error 404
            </div>
            
            <h2 className="text-3xl md:text-5xl font-bold text-foreground tracking-tight">
              Page Not Found
            </h2>
            
            <p className="text-xl text-muted-foreground leading-relaxed">
              We couldn't find the page you're looking for. It might have been moved, renamed, or doesn't exist.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Button asChild size="lg" className="btn-hero">
                <Link to="/">
                  <Home className="mr-2 h-5 w-5" />
                  Return Home
                </Link>
              </Button>
              
              <Button variant="outline" size="lg" onClick={() => { window.location.href = '/#contact'; setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }}>
                <ArrowLeft className="mr-2 h-5 w-5" />
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;